/**
 * AdminPulse · iter r97g · User M1520 · RIFATTO DA ZERO
 * ─────────────────────────────────────────────────────────────
 * L'utente ha chiesto un pannello essenziale:
 *   1. Errori del sistema (quali + quanti)
 *   2. Nuovi utenti al giorno (iscrizioni)
 *
 * Tutto il resto è stato tolto. Solo dati REALI dal backend:
 *   · GET /api/admin/errors      (dati raccolti dal middleware ErrorLogger)
 *   · GET /api/admin/stats       (signups da collezione users)
 *
 * Nessun mock, niente cazzate. Se qualcosa è vuoto, mostra "0" o
 * "Nessun errore" — la verità.
 *
 * Il vecchio pannello con 12+ sezioni è ancora su /dashboard/admin/avanzato.
 */
import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import AppShell from "@/components/AppShell";
import { Warning, Users, ArrowClockwise, Lock, Bug, CreditCard, ShieldWarning, CheckCircle } from "@phosphor-icons/react";

// ─── Palette locale ─────────────────────────────────────────────
const BRAND_SAGE = "#7C8B6E";
const INK = "#1C1C1A";
const MUTED = "#6B6A65";
const RED = "#C0533A";
const BORDER = "#E5E7E0";
const CARD = "#FFFFFF";

// ─── Formatter ─────────────────────────────────────────────────
function timeAgo(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const now = new Date();
  const diffSec = Math.floor((now - d) / 1000);
  if (diffSec < 60) return `${diffSec}s fa`;
  const m = Math.floor(diffSec / 60);
  if (m < 60) return `${m} min fa`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} h fa`;
  const g = Math.floor(h / 24);
  return `${g} g fa`;
}

function shortTs(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });
}

// ─── Bar chart minimo (SVG) ─────────────────────────────────────
function BarChart({ data, xKey, yKey, height = 80, color = BRAND_SAGE, testid }) {
  if (!data || data.length === 0) {
    return (
      <div className="text-[13px] text-[#9A968B] py-6 text-center" data-testid={`${testid}-empty`}>
        Nessun dato ancora.
      </div>
    );
  }
  const max = Math.max(1, ...data.map((d) => d[yKey] || 0));
  const W = 100;
  const barW = W / data.length;
  return (
    <svg
      viewBox={`0 0 ${W} ${height}`}
      preserveAspectRatio="none"
      className="w-full block"
      style={{ height }}
      data-testid={testid}
    >
      {data.map((d, i) => {
        const v = d[yKey] || 0;
        const h = (v / max) * (height - 8);
        return (
          <g key={i}>
            <rect
              x={i * barW + 0.6}
              y={height - h - 2}
              width={barW - 1.2}
              height={h}
              fill={v > 0 ? color : "#E5E7E0"}
              opacity={v > 0 ? 1 : 0.5}
            />
            {v > 0 && v === max && (
              <text
                x={i * barW + barW / 2}
                y={height - h - 4}
                textAnchor="middle"
                style={{ fontSize: 5, fill: INK, fontWeight: 700 }}
              >
                {v}
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
}

// ─── Componente principale ──────────────────────────────────────
export default function AdminPulse() {
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [errData, setErrData] = useState(null);
  const [statsData, setStatsData] = useState(null);
  const [payData, setPayData] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [expandedGroup, setExpandedGroup] = useState(null);
  const [errorDetail, setErrorDetail] = useState(null);

  const load = useCallback(async () => {
    try {
      setRefreshing(true);
      const [errRes, statsRes, payRes] = await Promise.all([
        api.get("/admin/errors?hours=24"),
        api.get("/admin/stats"),
        api.get("/admin/payments").catch(() => ({ data: null })),
      ]);
      setErrData(errRes.data);
      setStatsData(statsRes.data);
      setPayData(payRes.data);
    } catch (e) {
      if (e?.response?.status === 403) setForbidden(true);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh ogni 60s
  useEffect(() => {
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  const showDetail = async (ts) => {
    try {
      const { data } = await api.get(`/admin/errors/${encodeURIComponent(ts)}`);
      setErrorDetail(data);
    } catch { /* */ }
  };

  // ─── FORBIDDEN ─────────────────────────────────────────────
  if (forbidden) {
    return (
      <AppShell>
        <div className="max-w-md mx-auto py-24 px-6 text-center">
          <Lock size={40} weight="duotone" className="mx-auto mb-4 text-[#9A968B]" />
          <div className="font-editorial text-2xl text-[#1C1C1A] mb-2">Accesso riservato</div>
          <div className="text-sm text-[#6B6A65]">
            Questa pagina è visibile solo al team Pronto.
          </div>
        </div>
      </AppShell>
    );
  }

  // ─── LOADING ───────────────────────────────────────────────
  if (loading) {
    return (
      <AppShell>
        <div className="max-w-4xl mx-auto py-20 px-6 text-center text-sm text-[#6B6A65]">
          Carico...
        </div>
      </AppShell>
    );
  }

  const err = errData?.summary || {};
  const trend24h = errData?.trend_24h || [];
  const grouped = errData?.grouped || [];
  const recent = errData?.recent || [];

  const s = statsData?.summary || {};
  const signupsTrend = statsData?.daily_trend || [];
  const recentUsers = statsData?.recent_users || [];

  const errorCount24h = err.total_24h || 0;
  const errorCount1h = err.total_1h || 0;
  const isHealthy = errorCount1h === 0;

  return (
    <AppShell>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 pt-6 pb-24" data-testid="admin-pulse-v2">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="text-[11px] uppercase tracking-[0.24em] text-[#6B6A65] font-semibold">
              Pannello controllo
            </div>
            <h1
              className="font-editorial text-[#1C1C1A] mt-2"
              style={{ fontSize: 34, lineHeight: 1 }}
            >
              Pronto
            </h1>
          </div>
          <button
            onClick={load}
            disabled={refreshing}
            className="flex items-center gap-2 px-3 py-2 text-[12px] font-medium text-[#6B6A65] border border-[#E5E7E0] bg-white hover:bg-[#F1F4ED] disabled:opacity-50 transition-colors"
            data-testid="admin-refresh-btn"
            aria-label="Aggiorna dati"
          >
            <ArrowClockwise
              size={14}
              weight="bold"
              className={refreshing ? "animate-spin" : ""}
            />
            Aggiorna
          </button>
        </div>

        {/* ═══════ 1. ERRORI ═══════ */}
        <section className="mb-10" data-testid="admin-errors-section">
          <div className="flex items-baseline justify-between mb-4">
            <div className="flex items-center gap-2">
              <Bug size={18} weight="duotone" className={isHealthy ? "text-[#7C8B6E]" : "text-[#C0533A]"} />
              <h2 className="font-editorial text-xl text-[#1C1C1A]">
                Errori del sistema
              </h2>
            </div>
            <div className="text-[11px] text-[#9A968B]">ultime 24 ore</div>
          </div>

          {/* Hero numeri */}
          <div className="grid grid-cols-2 gap-3 mb-6">
            <div
              className={`p-5 border ${isHealthy ? "bg-[#F1F4ED] border-[#7C8B6E]/30" : "bg-[#FDF3EF] border-[#C0533A]/30"}`}
              data-testid="admin-error-hero-1h"
            >
              <div className="text-[10.5px] uppercase tracking-[0.2em] text-[#6B6A65] font-semibold">
                Ultima ora
              </div>
              <div
                className="mt-2 font-editorial"
                style={{
                  fontSize: 44, lineHeight: 1,
                  color: isHealthy ? BRAND_SAGE : RED,
                }}
              >
                {errorCount1h}
              </div>
              <div className="text-[12px] text-[#6B6A65] mt-2">
                {isHealthy ? "Tutto ok" : "Errori in corso"}
              </div>
            </div>
            <div className="p-5 bg-white border border-[#E5E7E0]" data-testid="admin-error-hero-24h">
              <div className="text-[10.5px] uppercase tracking-[0.2em] text-[#6B6A65] font-semibold">
                Totale 24h
              </div>
              <div className="mt-2 font-editorial text-[#1C1C1A]"
                   style={{ fontSize: 44, lineHeight: 1 }}>
                {errorCount24h}
              </div>
              <div className="text-[12px] text-[#6B6A65] mt-2">
                {err.last_error_ts
                  ? <>Ultimo: <span className="text-[#1C1C1A] font-medium">{timeAgo(err.last_error_ts)}</span></>
                  : "Nessun errore"}
              </div>
            </div>
          </div>

          {/* Grafico oraria 24h */}
          {errorCount24h > 0 && (
            <div className="p-4 bg-white border border-[#E5E7E0] mb-6">
              <div className="flex items-baseline justify-between mb-2">
                <div className="text-[10.5px] uppercase tracking-[0.2em] text-[#6B6A65] font-semibold">
                  Errori per ora
                </div>
              </div>
              <BarChart
                data={trend24h}
                xKey="hour"
                yKey="count"
                color={RED}
                testid="admin-error-hourly-chart"
              />
              <div className="flex justify-between text-[9px] text-[#9A968B] mt-1">
                <span>-24h</span>
                <span>-12h</span>
                <span>ora</span>
              </div>
            </div>
          )}

          {/* Grouped */}
          {grouped.length > 0 ? (
            <div className="bg-white border border-[#E5E7E0]" data-testid="admin-error-grouped">
              <div className="px-4 py-3 border-b border-[#E5E7E0]">
                <div className="text-[10.5px] uppercase tracking-[0.2em] text-[#6B6A65] font-semibold">
                  Errori raggruppati
                </div>
              </div>
              <ul className="divide-y divide-[#E5E7E0]">
                {grouped.slice(0, 15).map((g, i) => {
                  const key = `${g.path}::${g.exc_type}`;
                  const isOpen = expandedGroup === key;
                  return (
                    <li key={i} data-testid={`admin-error-group-${i}`}>
                      <button
                        onClick={() => setExpandedGroup(isOpen ? null : key)}
                        className="w-full text-left px-4 py-3 hover:bg-[#F1F4ED] transition-colors"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-[12.5px] font-mono text-[#C0533A] font-semibold">
                                {g.exc_type}
                              </span>
                              <span className="text-[11px] text-[#9A968B]">·</span>
                              <span className="text-[12px] text-[#1C1C1A] font-medium truncate">
                                {g.path}
                              </span>
                            </div>
                            {isOpen && g.last_message && (
                              <div className="mt-2 text-[12px] text-[#6B6A65] font-mono bg-[#F8F6F1] p-2 border-l-2 border-[#C0533A]/50">
                                {g.last_message}
                              </div>
                            )}
                            {isOpen && (
                              <div className="mt-2 text-[11px] text-[#9A968B]">
                                Ultimo: {timeAgo(g.last_ts)} · {shortTs(g.last_ts)}
                              </div>
                            )}
                          </div>
                          <div className="text-right shrink-0">
                            <div className="font-editorial text-2xl text-[#C0533A] tabular-nums leading-none">
                              {g.count}
                            </div>
                            <div className="text-[9px] text-[#9A968B] uppercase tracking-wider mt-1">
                              {g.count === 1 ? "volta" : "volte"}
                            </div>
                          </div>
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          ) : (
            <div className="p-8 bg-[#F1F4ED] border border-[#7C8B6E]/20 text-center">
              <div className="text-[13px] text-[#5A6250]">
                🎉 <span className="font-medium">Nessun errore nelle ultime 24 ore.</span>
              </div>
            </div>
          )}

          {/* Ultimi errori */}
          {recent.length > 0 && (
            <details className="mt-4" data-testid="admin-error-recent-details">
              <summary className="cursor-pointer text-[12px] text-[#6B6A65] hover:text-[#1C1C1A]">
                Vedi ultimi {Math.min(recent.length, 20)} errori in ordine cronologico
              </summary>
              <ul className="mt-3 divide-y divide-[#E5E7E0] bg-white border border-[#E5E7E0]">
                {recent.slice(0, 20).map((e, i) => (
                  <li key={i}>
                    <button
                      onClick={() => showDetail(e.ts)}
                      className="w-full text-left px-4 py-2.5 hover:bg-[#F1F4ED] flex items-center justify-between gap-3"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-[11.5px] font-mono text-[#C0533A] font-semibold">
                            {e.exc_type}
                          </span>
                          <span className="text-[11.5px] text-[#1C1C1A] truncate">
                            {e.method} {e.path}
                          </span>
                        </div>
                        <div className="text-[10.5px] text-[#9A968B] font-mono truncate mt-0.5">
                          {e.exc_message?.slice(0, 90)}
                        </div>
                      </div>
                      <div className="text-[11px] text-[#9A968B] tabular-nums shrink-0">
                        {timeAgo(e.ts)}
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            </details>
          )}
        </section>

        {/* ═══════ 2. NUOVI UTENTI ═══════ */}
        <section data-testid="admin-signups-section">
          <div className="flex items-baseline justify-between mb-4">
            <div className="flex items-center gap-2">
              <Users size={18} weight="duotone" className="text-[#7C8B6E]" />
              <h2 className="font-editorial text-xl text-[#1C1C1A]">
                Nuovi utenti
              </h2>
            </div>
            <div className="text-[11px] text-[#9A968B]">ultimi 14 giorni</div>
          </div>

          <div className="grid grid-cols-3 gap-3 mb-6">
            <div className="p-5 bg-[#F1F4ED] border border-[#7C8B6E]/30" data-testid="admin-signup-today">
              <div className="text-[10.5px] uppercase tracking-[0.2em] text-[#6B6A65] font-semibold">
                Oggi
              </div>
              <div className="mt-2 font-editorial text-[#5A6250]"
                   style={{ fontSize: 44, lineHeight: 1 }}>
                {s.users_today ?? 0}
              </div>
            </div>
            <div className="p-5 bg-white border border-[#E5E7E0]" data-testid="admin-signup-7d">
              <div className="text-[10.5px] uppercase tracking-[0.2em] text-[#6B6A65] font-semibold">
                7 giorni
              </div>
              <div className="mt-2 font-editorial text-[#1C1C1A]"
                   style={{ fontSize: 44, lineHeight: 1 }}>
                {s.users_7d ?? 0}
              </div>
            </div>
            <div className="p-5 bg-white border border-[#E5E7E0]" data-testid="admin-signup-total">
              <div className="text-[10.5px] uppercase tracking-[0.2em] text-[#6B6A65] font-semibold">
                Totale
              </div>
              <div className="mt-2 font-editorial text-[#1C1C1A]"
                   style={{ fontSize: 44, lineHeight: 1 }}>
                {s.total_users ?? 0}
              </div>
            </div>
          </div>

          {/* Grafico signups 14gg */}
          <div className="p-4 bg-white border border-[#E5E7E0] mb-6">
            <div className="text-[10.5px] uppercase tracking-[0.2em] text-[#6B6A65] font-semibold mb-2">
              Iscrizioni per giorno
            </div>
            <BarChart
              data={signupsTrend}
              xKey="date"
              yKey="count"
              color={BRAND_SAGE}
              testid="admin-signup-chart"
              height={100}
            />
            <div className="flex justify-between text-[9px] text-[#9A968B] mt-1">
              <span>-14g</span>
              <span>-7g</span>
              <span>oggi</span>
            </div>
          </div>

          {/* Recent signups */}
          {recentUsers.length > 0 && (
            <div className="bg-white border border-[#E5E7E0]" data-testid="admin-recent-signups">
              <div className="px-4 py-3 border-b border-[#E5E7E0]">
                <div className="text-[10.5px] uppercase tracking-[0.2em] text-[#6B6A65] font-semibold">
                  Ultime iscrizioni
                </div>
              </div>
              <ul className="divide-y divide-[#E5E7E0]">
                {recentUsers.slice(0, 12).map((u, i) => (
                  <li key={i} className="px-4 py-2.5 flex items-center justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-[13px] text-[#1C1C1A] truncate">
                          {u.email}
                        </span>
                        {u.email_verified ? null : (
                          <span className="text-[9px] px-1.5 py-0.5 bg-[#F8F6F1] text-[#9A968B] border border-[#E5E7E0] uppercase tracking-wider">
                            non verificato
                          </span>
                        )}
                      </div>
                      {u.name && (
                        <div className="text-[11px] text-[#9A968B] truncate">{u.name}</div>
                      )}
                    </div>
                    <div className="text-[11px] text-[#9A968B] tabular-nums shrink-0">
                      {u.created_at?.slice(5, 10).replace("-", "/")}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </section>

        {/* ═══════ 3. PAGAMENTI STRIPE ═══════ */}
        {payData && (
          <section className="mt-10" data-testid="admin-payments-section">
            <div className="flex items-baseline justify-between mb-4">
              <div className="flex items-center gap-2">
                <CreditCard size={18} weight="duotone" className="text-[#7C8B6E]" />
                <h2 className="font-editorial text-xl text-[#1C1C1A]">
                  Pagamenti
                </h2>
              </div>
              <div className="text-[11px] text-[#9A968B]">Stripe · live</div>
            </div>

            {!payData.configured ? (
              <div className="p-6 bg-[#FDF3EF] border border-[#C0533A]/30 text-[13px] text-[#1C1C1A]">
                <div className="font-semibold text-[#C0533A]">Stripe non configurato</div>
                <div className="text-[#6B6A65] mt-1">{payData.reason || "Chiave API mancante"}</div>
              </div>
            ) : (
              <>
                {/* Alert env problems */}
                {(!payData.env_check?.livemode || payData.webhook_health?.duplicated || (payData.api_errors && payData.api_errors.length > 0)) && (
                  <div className="mb-4 p-3 bg-[#FDF3EF] border border-[#C0533A]/30 text-[12px] text-[#1C1C1A]">
                    <div className="flex items-start gap-2">
                      <ShieldWarning size={16} weight="duotone" className="text-[#C0533A] shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <div className="font-semibold text-[#C0533A]">Attenzione configurazione</div>
                        <ul className="mt-1 list-disc list-inside text-[#6B6A65] space-y-0.5">
                          {!payData.env_check?.livemode && (
                            <li>La chiave Stripe non è LIVE — è in modalità TEST</li>
                          )}
                          {payData.webhook_health?.duplicated && (
                            <li>
                              <strong className="text-[#C0533A]">Webhook duplicati</strong>: {payData.webhook_health.endpoints_count} endpoint attivi verso lo stesso URL — vai su Stripe Dashboard → Webhooks e cancella quelli in eccesso
                            </li>
                          )}
                          {(payData.api_errors || []).map((e, i) => (
                            <li key={i} className="font-mono text-[10.5px]">{e}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                )}

                {/* Hero numeri */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
                  <div className="p-4 bg-[#F1F4ED] border border-[#7C8B6E]/30" data-testid="pay-hero-mrr">
                    <div className="text-[10.5px] uppercase tracking-[0.2em] text-[#6B6A65] font-semibold">
                      MRR
                    </div>
                    <div className="mt-2 font-editorial text-[#5A6250]"
                         style={{ fontSize: 36, lineHeight: 1 }}>
                      €{Number(payData.summary?.mrr_eur || 0).toLocaleString("it-IT", { maximumFractionDigits: 0 })}
                    </div>
                    <div className="text-[11px] text-[#6B6A65] mt-1.5 leading-snug">
                      ricorrenti/mese
                    </div>
                  </div>
                  <div className="p-4 bg-white border border-[#E5E7E0]" data-testid="pay-hero-subs">
                    <div className="text-[10.5px] uppercase tracking-[0.2em] text-[#6B6A65] font-semibold">
                      Abbonati
                    </div>
                    <div className="mt-2 font-editorial text-[#1C1C1A]"
                         style={{ fontSize: 36, lineHeight: 1 }}>
                      {payData.summary?.subscriptions_active || 0}
                    </div>
                    <div className="text-[11px] text-[#6B6A65] mt-1.5">attivi ora</div>
                  </div>
                  <div className="p-4 bg-white border border-[#E5E7E0]" data-testid="pay-hero-month">
                    <div className="text-[10.5px] uppercase tracking-[0.2em] text-[#6B6A65] font-semibold">
                      Mese corrente
                    </div>
                    <div className="mt-2 font-editorial text-[#1C1C1A]"
                         style={{ fontSize: 36, lineHeight: 1 }}>
                      €{Number(payData.summary?.revenue_this_month_eur || 0).toLocaleString("it-IT", { maximumFractionDigits: 0 })}
                    </div>
                    <div className="text-[11px] text-[#6B6A65] mt-1.5">incassati</div>
                  </div>
                  <div className="p-4 bg-white border border-[#E5E7E0]" data-testid="pay-hero-30d">
                    <div className="text-[10.5px] uppercase tracking-[0.2em] text-[#6B6A65] font-semibold">
                      Ultimi 30gg
                    </div>
                    <div className="mt-2 font-editorial text-[#1C1C1A]"
                         style={{ fontSize: 36, lineHeight: 1 }}>
                      €{Number(payData.summary?.revenue_last_30d_eur || 0).toLocaleString("it-IT", { maximumFractionDigits: 0 })}
                    </div>
                    <div className="text-[11px] text-[#6B6A65] mt-1.5">
                      {payData.summary?.charges_count_30d || 0} pagamenti
                    </div>
                  </div>
                </div>

                {/* Health indicators */}
                <div className="p-4 bg-white border border-[#E5E7E0] mb-6" data-testid="pay-health-strip">
                  <div className="text-[10.5px] uppercase tracking-[0.2em] text-[#6B6A65] font-semibold mb-3">
                    Diagnosi
                  </div>
                  <ul className="space-y-2 text-[12.5px]">
                    <li className="flex items-center gap-2">
                      {payData.env_check?.livemode
                        ? <CheckCircle size={14} weight="fill" className="text-[#7C8B6E] shrink-0" />
                        : <Warning size={14} weight="duotone" className="text-[#C0533A] shrink-0" />}
                      <span>
                        Chiave Stripe: <span className="font-mono text-[11px] bg-[#F8F6F1] px-1.5 py-0.5">{payData.env_check?.api_key_prefix}...</span>
                        {" "}·{" "}
                        {payData.env_check?.livemode ? <span className="text-[#5A6250] font-semibold">LIVE</span> : <span className="text-[#C0533A] font-semibold">TEST</span>}
                      </span>
                    </li>
                    <li className="flex items-center gap-2">
                      {payData.env_check?.webhook_secret_set
                        ? <CheckCircle size={14} weight="fill" className="text-[#7C8B6E] shrink-0" />
                        : <Warning size={14} weight="duotone" className="text-[#C0533A] shrink-0" />}
                      <span>Webhook secret in .env: <strong>{payData.env_check?.webhook_secret_set ? "configurato" : "MANCANTE"}</strong></span>
                    </li>
                    <li className="flex items-center gap-2">
                      {payData.env_check?.price_ids_configured === 6
                        ? <CheckCircle size={14} weight="fill" className="text-[#7C8B6E] shrink-0" />
                        : <Warning size={14} weight="duotone" className="text-[#C8A66B] shrink-0" />}
                      <span>Price IDs configurati: <strong>{payData.env_check?.price_ids_configured}/6</strong></span>
                    </li>
                    <li className="flex items-center gap-2">
                      {payData.webhook_health?.enabled_count > 0 && !payData.webhook_health?.duplicated
                        ? <CheckCircle size={14} weight="fill" className="text-[#7C8B6E] shrink-0" />
                        : <Warning size={14} weight="duotone" className={payData.webhook_health?.duplicated ? "text-[#C0533A] shrink-0" : "text-[#C8A66B] shrink-0"} />}
                      <span>
                        Webhook Stripe: <strong>{payData.webhook_health?.enabled_count || 0} attivi</strong>
                        {payData.webhook_health?.duplicated && <span className="text-[#C0533A]"> (duplicati!)</span>}
                      </span>
                    </li>
                    <li className="flex items-center gap-2">
                      {payData.summary?.events_received_30d > 0
                        ? <CheckCircle size={14} weight="fill" className="text-[#7C8B6E] shrink-0" />
                        : <Warning size={14} weight="duotone" className="text-[#C8A66B] shrink-0" />}
                      <span>Eventi Stripe ultimi 30gg: <strong>{payData.summary?.events_received_30d || 0}</strong></span>
                    </li>
                  </ul>
                </div>

                {/* Ultimi pagamenti */}
                {payData.recent_charges && payData.recent_charges.length > 0 ? (
                  <div className="bg-white border border-[#E5E7E0]" data-testid="pay-recent-list">
                    <div className="px-4 py-3 border-b border-[#E5E7E0]">
                      <div className="text-[10.5px] uppercase tracking-[0.2em] text-[#6B6A65] font-semibold">
                        Ultimi pagamenti reali
                      </div>
                    </div>
                    <ul className="divide-y divide-[#E5E7E0]">
                      {payData.recent_charges.slice(0, 15).map((c, i) => (
                        <li key={c.id || i} className="px-4 py-3 flex items-center justify-between gap-3">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              <span className="text-[13px] text-[#1C1C1A] truncate">
                                {c.customer_email || "—"}
                              </span>
                              <span className="text-[9px] px-1.5 py-0.5 bg-[#F1F4ED] text-[#5A6250] border border-[#7C8B6E]/30 uppercase tracking-wider">
                                {c.status}
                              </span>
                            </div>
                            {c.description && (
                              <div className="text-[11px] text-[#9A968B] truncate mt-0.5">{c.description}</div>
                            )}
                          </div>
                          <div className="text-right shrink-0">
                            <div className="font-editorial text-lg text-[#1C1C1A] tabular-nums leading-none">
                              €{c.amount_eur.toLocaleString("it-IT", { minimumFractionDigits: 2 })}
                            </div>
                            <div className="text-[10px] text-[#9A968B] mt-1">
                              {new Date(c.created).toLocaleDateString("it-IT", { day: "2-digit", month: "short" })}
                            </div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : (
                  <div className="p-6 bg-[#F8F6F1] border border-[#E5E7E0] text-center">
                    <div className="text-[13px] text-[#6B6A65]">
                      Nessun pagamento negli ultimi 30 giorni.
                    </div>
                    <div className="text-[11px] text-[#9A968B] mt-1">
                      Fai un test acquisto per verificare che il flow funzioni end-to-end.
                    </div>
                  </div>
                )}
              </>
            )}
          </section>
        )}

        {/* Footer */}
        <div className="mt-8 text-[10.5px] text-[#9A968B] text-center">
          Aggiorna automatico ogni 60 secondi ·
          Dati letti da MongoDB in tempo reale ·
          <a href="/dashboard/admin/avanzato" className="ml-1 underline hover:text-[#1C1C1A]">
            pannello avanzato →
          </a>
        </div>

        {/* Modal detail errore */}
        {errorDetail && (
          <div
            className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
            onClick={() => setErrorDetail(null)}
            data-testid="admin-error-detail-modal"
          >
            <div
              className="bg-white max-w-2xl w-full max-h-[80vh] overflow-auto border border-[#E5E7E0]"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-5 border-b border-[#E5E7E0] flex items-start justify-between">
                <div>
                  <div className="text-[10.5px] uppercase tracking-[0.2em] text-[#6B6A65] font-semibold mb-2">
                    Dettaglio errore
                  </div>
                  <div className="text-[15px] font-mono text-[#C0533A] font-semibold">
                    {errorDetail.exc_type}
                  </div>
                  <div className="text-[13px] text-[#1C1C1A] mt-1">
                    {errorDetail.method} {errorDetail.path}
                  </div>
                </div>
                <button
                  onClick={() => setErrorDetail(null)}
                  className="text-[#9A968B] hover:text-[#1C1C1A] text-2xl leading-none"
                >
                  ×
                </button>
              </div>
              <div className="p-5 space-y-4">
                <div>
                  <div className="text-[10.5px] uppercase tracking-[0.2em] text-[#6B6A65] font-semibold mb-1">
                    Messaggio
                  </div>
                  <div className="text-[13px] font-mono text-[#1C1C1A] bg-[#F8F6F1] p-3 border-l-2 border-[#C0533A]">
                    {errorDetail.exc_message || "—"}
                  </div>
                </div>
                {errorDetail.traceback && (
                  <div>
                    <div className="text-[10.5px] uppercase tracking-[0.2em] text-[#6B6A65] font-semibold mb-1">
                      Traceback
                    </div>
                    <pre className="text-[10.5px] font-mono text-[#6B6A65] bg-[#F8F6F1] p-3 overflow-auto max-h-64 whitespace-pre-wrap">
{errorDetail.traceback}
                    </pre>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-3 text-[12px]">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-[#9A968B]">Quando</div>
                    <div className="text-[#1C1C1A] mt-0.5">
                      {new Date(errorDetail.ts).toLocaleString("it-IT")}
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-[#9A968B]">Status</div>
                    <div className="text-[#1C1C1A] mt-0.5">{errorDetail.status_code}</div>
                  </div>
                  {errorDetail.user_id && (
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-[#9A968B]">Utente</div>
                      <div className="text-[#1C1C1A] mt-0.5 font-mono text-[11px]">{errorDetail.user_id}</div>
                    </div>
                  )}
                  {errorDetail.client_ip && (
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-[#9A968B]">IP</div>
                      <div className="text-[#1C1C1A] mt-0.5 font-mono text-[11px]">{errorDetail.client_ip}</div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </AppShell>
  );
}
